import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  Timestamp,
  runTransaction,
  doc,
  getDoc,
  getDocs,
  getFirestore
} from 'firebase/firestore';
import { APP_ID } from '../config/constants';
import { checkChange } from '../ledger/effects';

/**
 * Get Firestore instance
 */
const getDb = () => getFirestore();

/**
 * Pay credit card debt
 * Decreases credit card usedCredit and decreases source wallet balance
 * This is NOT counted as an expense (to avoid double-counting)
 */
export const payCreditCard = async (userId, paymentData) => {
  const { 
    creditCardId, 
    sourceWalletId, 
    amount, 
    note = '', 
    date = new Date() 
  } = paymentData;

  try {
    // Validate amount
    if (!amount || amount <= 0) {
      return { success: false, error: 'Số tiền phải lớn hơn 0' };
    }

    // Validate IDs
    if (!creditCardId || !sourceWalletId) {
      return { success: false, error: 'Vui lòng chọn thẻ tín dụng và nguồn thanh toán' };
    }

    // Cannot pay from credit card to credit card
    if (creditCardId === sourceWalletId) {
      return { success: false, error: 'Không thể thanh toán từ chính thẻ tín dụng đó' };
    }

    // Use transaction to ensure data consistency
    const db = getDb();
    const paymentRef = await runTransaction(db, async (transaction) => {
      // Get credit card data
      const creditCardRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${creditCardId}`);
      const creditCardSnap = await transaction.get(creditCardRef);
      
      if (!creditCardSnap.exists()) {
        throw new Error('Thẻ tín dụng không tồn tại');
      }

      const creditCardData = creditCardSnap.data();
      
      if (creditCardData.type !== 'credit') {
        throw new Error('Nguồn được chọn không phải là thẻ tín dụng');
      }

      const currentUsed = creditCardData.usedCredit || 0;
      
      if (amount > currentUsed) {
        throw new Error(`Số tiền thanh toán (${amount.toLocaleString()}) vượt quá dư nợ hiện tại (${currentUsed.toLocaleString()})`);
      }

      // Get source wallet data
      const sourceWalletRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${sourceWalletId}`);
      const sourceWalletSnap = await transaction.get(sourceWalletRef);
      
      if (!sourceWalletSnap.exists()) {
        throw new Error('Nguồn thanh toán không tồn tại');
      }

      const sourceWalletData = sourceWalletSnap.data();
      
      // Check if source is also a credit card
      if (sourceWalletData.type === 'credit') {
        throw new Error('Không thể thanh toán thẻ tín dụng bằng thẻ tín dụng khác');
      }

      const currentBalance = sourceWalletData.balance || 0;
      
      // Cùng chính sách với sổ cái: ví ngân hàng chặn khi thiếu tiền, ví tiền mặt được âm (kèm cảnh báo ở Tổng quan)
      const policy = checkChange(sourceWalletData, 'balance', -amount);
      if (!policy.ok) throw new Error(policy.error);

      // Create payment record
      const paymentCollectionRef = collection(db, `artifacts/${APP_ID}/users/${userId}/creditPayments`);
      const newPaymentRef = doc(paymentCollectionRef);
      
      const paymentRecord = {
        creditCardId,
        creditCardName: creditCardData.name,
        sourceWalletId,
        sourceWalletName: sourceWalletData.name,
        amount,
        note,
        date: Timestamp.fromDate(new Date(date)),
        createdAt: Timestamp.now()
      };

      transaction.set(newPaymentRef, paymentRecord);

      // Update credit card - decrease usedCredit
      const newUsedCredit = currentUsed - amount;
      transaction.update(creditCardRef, {
        usedCredit: newUsedCredit,
        updatedAt: Timestamp.now()
      });

      // Update source wallet - decrease balance
      const newBalance = currentBalance - amount;
      transaction.update(sourceWalletRef, {
        balance: newBalance,
        updatedAt: Timestamp.now()
      });

      return newPaymentRef;
    });

    return { 
      success: true, 
      data: { id: paymentRef.id },
      message: 'Thanh toán thẻ tín dụng thành công'
    };

  } catch (error) {
    console.error('Error paying credit card:', error);
    return { 
      success: false, 
      error: error.message || 'Có lỗi xảy ra khi thanh toán thẻ tín dụng'
    };
  }
};

/**
 * Subscribe to credit payments in real-time
 */
/**
 * Lấy các thanh toán thẻ trong một tháng (one-shot) — dùng cho dòng
 * "Nghĩa vụ tháng này" ở trang chủ, cùng nguồn với dải trên trang Tài sản.
 */
export const getCreditPaymentsByMonth = async (userId, year, month) => {
  try {
    const db = getDb();
    const paymentsRef = collection(db, `artifacts/${APP_ID}/users/${userId}/creditPayments`);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 1);
    const q = query(
      paymentsRef,
      where('date', '>=', Timestamp.fromDate(start)),
      where('date', '<', Timestamp.fromDate(end))
    );
    const snapshot = await getDocs(q);
    return {
      success: true,
      data: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    };
  } catch (error) {
    console.error('Error getting credit payments by month:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const subscribeToCreditPayments = (userId, callback) => {
  if (!userId) {
    callback({ success: false, error: 'User ID is required' });
    return () => {};
  }

  try {
    const db = getDb();
    const paymentsRef = collection(db, `artifacts/${APP_ID}/users/${userId}/creditPayments`);
    const q = query(paymentsRef, orderBy('date', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const payments = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate(),
          createdAt: doc.data().createdAt?.toDate()
        }));

        callback({ success: true, data: payments });
      },
      (error) => {
        console.error('Error subscribing to credit payments:', error);
        callback({ success: false, error: error.message });
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up credit payments subscription:', error);
    callback({ success: false, error: error.message });
    return () => {};
  }
};

/**
 * Get credit payment history for a specific credit card
 */
export const getCreditPaymentHistory = async (userId, creditCardId, startDate = null, endDate = null) => {
  try {
    if (!userId || !creditCardId) {
      return { success: false, error: 'User ID and Credit Card ID are required' };
    }

    const db = getDb();
    const paymentsRef = collection(db, `artifacts/${APP_ID}/users/${userId}/creditPayments`);
    let q = query(
      paymentsRef,
      where('creditCardId', '==', creditCardId),
      orderBy('date', 'desc')
    );

    // Add date filters if provided
    if (startDate) {
      q = query(q, where('date', '>=', Timestamp.fromDate(new Date(startDate))));
    }
    if (endDate) {
      q = query(q, where('date', '<=', Timestamp.fromDate(new Date(endDate))));
    }

    const snapshot = await getDocs(q);
    const payments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate(),
      createdAt: doc.data().createdAt?.toDate()
    }));

    return { success: true, data: payments };
  } catch (error) {
    console.error('Error getting credit payment history:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get total payments for a credit card in a specific period
 */
export const getCreditPaymentStats = async (userId, creditCardId, year, month = null) => {
  try {
    if (!userId || !creditCardId) {
      return { success: false, error: 'User ID and Credit Card ID are required' };
    }

    const db = getDb();
    const paymentsRef = collection(db, `artifacts/${APP_ID}/users/${userId}/creditPayments`);
    
    // Set date range
    const startDate = month 
      ? new Date(year, month - 1, 1)
      : new Date(year, 0, 1);
    const endDate = month
      ? new Date(year, month, 0, 23, 59, 59)
      : new Date(year, 11, 31, 23, 59, 59);

    const q = query(
      paymentsRef,
      where('creditCardId', '==', creditCardId),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    
    let totalPaid = 0;
    const payments = [];

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      totalPaid += data.amount || 0;
      payments.push({
        id: doc.id,
        ...data,
        date: data.date?.toDate(),
        createdAt: data.createdAt?.toDate()
      });
    });

    return { 
      success: true, 
      data: {
        totalPaid,
        count: payments.length,
        payments
      }
    };
  } catch (error) {
    console.error('Error getting credit payment stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update credit payment
 * Adjusts usedCredit and balance based on amount difference
 */
export const updateCreditPayment = async (userId, paymentId, updateData) => {
  const { amount: newAmount, note, date } = updateData;

  try {
    // Validate amount
    if (!newAmount || newAmount <= 0) {
      return { success: false, error: 'Số tiền phải lớn hơn 0' };
    }

    const db = getDb();
    const paymentRef = doc(db, `artifacts/${APP_ID}/users/${userId}/creditPayments/${paymentId}`);

    // Use transaction to ensure data consistency
    await runTransaction(db, async (transaction) => {
      // Get current payment data
      const paymentSnap = await transaction.get(paymentRef);
      
      if (!paymentSnap.exists()) {
        throw new Error('Giao dịch thanh toán không tồn tại');
      }

      const oldPayment = paymentSnap.data();
      const oldAmount = oldPayment.amount || 0;
      const amountDifference = newAmount - oldAmount;

      // If amount changed, update credit card and source wallet
      if (amountDifference !== 0) {
        // Get credit card and source wallet refs
        const creditCardRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${oldPayment.creditCardId}`);
        const sourceWalletRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${oldPayment.sourceWalletId}`);

        // Get current data
        const creditCardSnap = await transaction.get(creditCardRef);
        const sourceWalletSnap = await transaction.get(sourceWalletRef);

        if (!creditCardSnap.exists() || !sourceWalletSnap.exists()) {
          throw new Error('Thẻ tín dụng hoặc nguồn thanh toán không tồn tại');
        }

        const creditCardData = creditCardSnap.data();
        const sourceWalletData = sourceWalletSnap.data();

        const currentUsed = creditCardData.usedCredit || 0;
        const currentBalance = sourceWalletData.balance || 0;

        // Calculate new values
        // If amountDifference > 0: pay more, decrease more usedCredit and balance
        // If amountDifference < 0: pay less, increase back usedCredit and balance
        const newUsedCredit = currentUsed - amountDifference;
        const newBalance = currentBalance - amountDifference;

        // Validate — hai vế phải đối xứng, KHÔNG kẹp một vế:
        // kẹp usedCredit ở 0 trong khi ví vẫn trừ đủ sẽ làm lệch sổ vĩnh viễn
        if (newUsedCredit < 0) {
          throw new Error(
            `Số tiền tăng thêm vượt quá dư nợ hiện tại của thẻ (${currentUsed.toLocaleString('vi-VN')} đ)`
          );
        }

        if (newBalance < 0 && sourceWalletData.type !== 'cash') {
          throw new Error('Số dư nguồn thanh toán không đủ');
        }

        // Update credit card
        transaction.update(creditCardRef, {
          usedCredit: newUsedCredit,
          updatedAt: Timestamp.now()
        });

        // Update source wallet
        transaction.update(sourceWalletRef, {
          balance: newBalance,
          updatedAt: Timestamp.now()
        });
      }

      // Update payment record
      const updatePayload = {
        amount: newAmount,
        updatedAt: Timestamp.now()
      };

      if (note !== undefined) {
        updatePayload.note = note;
      }

      if (date) {
        updatePayload.date = Timestamp.fromDate(new Date(date));
      }

      transaction.update(paymentRef, updatePayload);
    });

    return { 
      success: true, 
      message: 'Cập nhật giao dịch thanh toán thành công'
    };

  } catch (error) {
    console.error('Error updating credit payment:', error);
    return { 
      success: false, 
      error: error.message || 'Có lỗi xảy ra khi cập nhật giao dịch'
    };
  }
};

/**
 * Delete credit payment
 * Reverses the payment by increasing usedCredit and balance
 */
export const deleteCreditPayment = async (userId, paymentId) => {
  try {
    const db = getDb();
    const paymentRef = doc(db, `artifacts/${APP_ID}/users/${userId}/creditPayments/${paymentId}`);

    // Use transaction to ensure data consistency
    await runTransaction(db, async (transaction) => {
      // Get payment data
      const paymentSnap = await transaction.get(paymentRef);
      
      if (!paymentSnap.exists()) {
        throw new Error('Giao dịch thanh toán không tồn tại');
      }

      const payment = paymentSnap.data();
      const { creditCardId, sourceWalletId, amount } = payment;

      // Get credit card and source wallet refs
      const creditCardRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${creditCardId}`);
      const sourceWalletRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${sourceWalletId}`);

      // Get current data
      const creditCardSnap = await transaction.get(creditCardRef);
      const sourceWalletSnap = await transaction.get(sourceWalletRef);

      if (!creditCardSnap.exists() || !sourceWalletSnap.exists()) {
        throw new Error('Thẻ tín dụng hoặc nguồn thanh toán không tồn tại');
      }

      const creditCardData = creditCardSnap.data();
      const sourceWalletData = sourceWalletSnap.data();

      // Reverse the payment: increase usedCredit (undo payment)
      const currentUsed = creditCardData.usedCredit || 0;
      const newUsedCredit = currentUsed + amount;

      // Reverse the payment: increase balance (refund)
      const currentBalance = sourceWalletData.balance || 0;
      const newBalance = currentBalance + amount;

      // Update credit card - increase usedCredit (undo payment)
      transaction.update(creditCardRef, {
        usedCredit: newUsedCredit,
        updatedAt: Timestamp.now()
      });

      // Update source wallet - increase balance (refund)
      transaction.update(sourceWalletRef, {
        balance: newBalance,
        updatedAt: Timestamp.now()
      });

      // Delete payment record
      transaction.delete(paymentRef);
    });

    return { 
      success: true, 
      message: 'Xóa giao dịch thanh toán thành công'
    };

  } catch (error) {
    console.error('Error deleting credit payment:', error);
    return { 
      success: false, 
      error: error.message || 'Có lỗi xảy ra khi xóa giao dịch'
    };
  }
};
